import React, { useState, useEffect } from "react";
import {
  FaCheckCircle,
  FaSpinner,
  FaTimes,
  FaUpload,
  FaYoutube,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaEye,
  FaVideo,
  FaMicrophone,
  FaRedo,
  FaCreditCard,
} from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import DescriptionViewer from "../../../Common/DescriptionViewer";
import ConferenceSubNav from "../../../Common/ConferenceSubNav";

const AcceptedFreeConference = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedConference, setSelectedConference] = useState(null);
  const [formData, setFormData] = useState({
    conferenceId: "",
    screenshot: null,
    channelName: "",
    videoLink: "",
    platform: "",
    duration: "",
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(conferences.length / itemsPerPage);

  const stats = {
    acceptedConferences: conferences.length,
    proofSubmitted: conferences.filter((conf) => conf.proofSubmitted).length,
    proofPending: conferences.filter((conf) => !conf.proofSubmitted).length,
  };

  const fetchAcceptedConferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/get/accepted/conferences`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const conferencesData = response.data?.data || [];
      const sortedConferences = [...conferencesData].sort((a, b) => {
        const dateA = new Date(a.acceptedAt || 0).getTime();
        const dateB = new Date(b.acceptedAt || 0).getTime();
        return dateB - dateA;
      });

      setConferences(sortedConferences);
      setError(null);
    } catch (err) {
      console.error("Error fetching accepted conferences:", err);
      setError("Failed to load accepted conferences.");
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`);
      if (response.data.success) {
        setAvailablePlatforms(response.data.data.plateforms || []);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
    }
  };

  useEffect(() => {
    fetchAcceptedConferences();
    fetchPlatforms();
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return [...conferences].slice(startIndex, endIndex);
  };

  const handleViewDetails = (conference) => {
    setSelectedConference(conference);
    setShowModal(true);
  };

  const handleSubmitProofClick = (conferenceId) => {
    setFormData((prev) => ({
      ...prev,
      conferenceId: conferenceId,
    }));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedConference(null);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData({
      conferenceId: "",
      screenshot: null,
      channelName: "",
      videoLink: "",
      platform: "",
      duration: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "duration") {
      let cleanValue = value.replace(/[^\d:]/g, "");
      const prevValue = formData.duration;
      const isDeleting = value.length < prevValue.length;
      if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      if (isDeleting && cleanValue.includes(":") && cleanValue.length <= 3) {
        cleanValue = cleanValue.replace(":", "");
      }
      if (!isDeleting && cleanValue.length === 2 && !cleanValue.includes(":")) {
        cleanValue = cleanValue + ":";
      }
      if (cleanValue.replace(":", "").length === 0) cleanValue = "";
      if (cleanValue.length > 5) cleanValue = cleanValue.substring(0, 5);
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      setFormData((prev) => ({ ...prev, screenshot: file }));
      setScreenshotPreview(URL.createObjectURL(file));
    }
  };

  const removeScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
      setScreenshotPreview(null);
    }
    setFormData((prev) => ({ ...prev, screenshot: null }));
    const fileInput = document.querySelector('input[name="screenshot"]');
    if (fileInput) fileInput.value = "";
  };

  const handleSubmitProof = async (e) => {
    e.preventDefault();
    if (!formData.screenshot || !formData.channelName || !formData.videoLink || !formData.platform || !formData.duration || !formData.conferenceId) {
      toast.error("Please fill all required fields");
      return;
    }

    const youtubePattern = /^(https?:\/\/)?(www\.)?(youtu\.be\/|youtube\.com\/(watch\?|embed\/|v\/|shorts\/)?)/i;
    const videoLink = formData.videoLink.trim();
    if (!youtubePattern.test(videoLink)) {
      toast.error("Please provide a valid YouTube link");
      return;
    }

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => submitData.append(key, formData[key]));
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/submit/conference/proof/${formData.conferenceId}`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.status === 200) {
        toast.success("Proof submitted successfully!");
        handleCloseModal();
        await fetchAcceptedConferences();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit proof");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24 text-slate-900">
      {/* Premium Compact Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-tight hover:bg-slate-100 transition-all whitespace-nowrap shadow-sm"
            >
              ← Go Back
            </button>
            <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full" />
            <div>
              <h1 className="text-lg font-black text-slate-900 leading-tight">Active Hub</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider leading-none">Free Coverage</span>
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                <span className="text-[9px] font-black text-blue-600 italic leading-none">{stats.acceptedConferences} Tasks</span>
              </div>
            </div>
          </div>
          <button
            onClick={fetchAcceptedConferences}
            disabled={loading}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 active:rotate-180 transition-all duration-500"
          >
            <FaRedo className={`text-[10px] ${loading ? "animate-spin text-blue-500" : ""}`} />
          </button>
        </div>
      </div>

      <ConferenceSubNav type="free" />

      <div className="max-w-5xl mx-auto p-3 space-y-3">
        {/* Stats Grid - Compact */}
        {!loading && !error && conferences.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white rounded-2xl p-3 border border-slate-100 shadow-sm text-center">
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Total</p>
              <p className="text-sm font-black text-slate-900 leading-none">{stats.acceptedConferences}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 border border-slate-100 shadow-sm text-center border-b-2 border-b-emerald-400">
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Submitted</p>
              <p className="text-sm font-black text-emerald-600 leading-none">{stats.proofSubmitted}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 border border-slate-100 shadow-sm text-center border-b-2 border-b-amber-400">
              <p className="text-[8px] font-black text-slate-400 uppercase mb-1">Pending</p>
              <p className="text-sm font-black text-amber-600 leading-none">{stats.proofPending}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-10 h-10 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin" />
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Accessing Vault...</p>
          </div>
        )}

        {/* Desktop View: Table (Ultra-Compact) */}
        {!loading && !error && (
          <div className="hidden lg:block bg-white rounded-[1.5rem] shadow-sm border border-slate-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50/50 text-slate-400 border-b border-slate-50">
                <tr>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Mission</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Status</th>
                  <th className="px-5 py-4 text-left text-[9px] font-black uppercase tracking-widest">Accepted At</th>
                  <th className="px-5 py-4 text-center text-[9px] font-black uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {getCurrentPageData().map((conference) => (
                  <tr key={conference._id} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center border border-blue-100/50">
                          <FaMicrophone className="text-blue-600 text-[10px]" />
                        </div>
                        <div>
                          <p className="font-black text-slate-800 text-xs leading-tight line-clamp-1 italic">{conference.conferenceDetails?.topic || "N/A"}</p>
                          <p className="text-[9px] font-bold text-slate-400 tabular-nums italic">Code #{conference.conferenceId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter ${conference.proofSubmitted ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-amber-50 text-amber-600 border border-amber-100"}`}>
                        {conference.proofSubmitted ? (conference.proofDetails?.adminRejectNote ? "Rejected" : "Submitted") : "Pending"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[10px] text-slate-400 font-bold italic tabular-nums">
                      {conference.acceptedAt ? new Date(conference.acceptedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button onClick={() => handleViewDetails(conference)} className="h-8 px-3 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5"><FaEye /> Details</button>
                        {(!conference.proofSubmitted || conference.proofDetails?.adminRejectNote) && (
                          <button onClick={() => handleSubmitProofClick(conference.conferenceId)} className={`h-8 px-3 text-white rounded-xl text-[9px] font-black uppercase tracking-widest active:scale-95 transition-all flex items-center gap-1.5 ${conference.proofDetails?.adminRejectNote ? "bg-rose-500" : "bg-blue-500"}`}><FaUpload /> {conference.proofDetails?.adminRejectNote ? "Resubmit" : "Submit"}</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile View: High-Density Widgets */}
        {!loading && !error && (
          <div className="lg:hidden max-w-md mx-auto space-y-3">
            {conferences.length > 0 ? (
              getCurrentPageData().map((conference) => (
                <div key={conference._id} className="relative bg-white rounded-[1.75rem] border border-slate-100 p-1 shadow-sm active:scale-[0.99] transition-all overflow-hidden border-b-2 border-b-blue-400/20">
                  <div className="bg-slate-50/40 rounded-[1.6rem] p-3.5 flex flex-col gap-3">
                    {/* Header */}
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 p-0.5 shadow-md shadow-blue-100/50 flex-shrink-0">
                        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                          <FaMicrophone className="text-blue-600 text-sm" />
                        </div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-[12px] font-black text-slate-900 leading-tight truncate pr-2 uppercase italic">{conference.conferenceDetails?.topic || "N/A"}</h3>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widths tabular-nums italic">Code #{conference.conferenceId}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${conference.proofSubmitted ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                            {conference.proofSubmitted ? (conference.proofDetails?.adminRejectNote ? "Rejected" : "Submitted") : "Pending"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Infos */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 text-[10px]"><FaCalendarAlt /></div>
                        <div>
                          <p className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1">Deployment</p>
                          <p className="text-[10px] font-black text-slate-900 leading-none tabular-nums italic">{conference.conferenceDetails?.conferenceDate}</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-2.5 border border-slate-100/50 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600 text-[10px]"><FaMapMarkerAlt /></div>
                        <div>
                          <p className="text-[7px] font-black text-slate-300 uppercase leading-none mb-1">Target Area</p>
                          <p className="text-[10px] font-black text-slate-900 leading-none truncate uppercase italic">{conference.conferenceDetails?.city || "Unknown"}</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions Group */}
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleViewDetails(conference)} className="flex-1 h-11 rounded-xl bg-slate-900 text-white shadow-lg shadow-slate-200/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest"><FaEye /> Details</button>
                      {(!conference.proofSubmitted || conference.proofDetails?.adminRejectNote) && (
                        <button onClick={() => handleSubmitProofClick(conference.conferenceId)} className={`flex-1 h-11 rounded-xl text-white shadow-lg shadow-blue-200/50 flex items-center justify-center gap-2 active:scale-95 transition-all text-[10px] font-black uppercase tracking-widest ${conference.proofDetails?.adminRejectNote ? "bg-rose-500" : "bg-blue-500"}`}><FaUpload /> {conference.proofDetails?.adminRejectNote ? "Resubmit" : "Submit"}</button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-10 text-center">
                <div className="w-16 h-16 rounded-[2rem] bg-white shadow-lg flex items-center justify-center mb-6">
                  <FaCheckCircle className="text-slate-100 text-3xl" />
                </div>
                <h2 className="text-base font-black text-slate-900 tracking-tight">Vault Empty</h2>
                <p className="text-[9px] font-bold text-slate-400 mt-2 px-6 uppercase tracking-wider italic leading-relaxed">No active free coverage projects in your current mission log.</p>
              </div>
            )}
          </div>
        )}

        {/* Compact Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 py-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-10 shadow-sm"
            >
              <FaRedo className="-rotate-90 text-[9px]" />
            </button>
            <div className="px-5 h-9 rounded-xl bg-slate-900 flex items-center gap-2.5 shadow-xl">
              <span className="text-[10px] font-black text-white italic">{currentPage}</span>
              <div className="w-0.5 h-0.5 rounded-full bg-slate-700" />
              <span className="text-[10px] font-black text-slate-500 italic">{totalPages}</span>
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-9 h-9 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-10 shadow-sm"
            >
              <FaRedo className="rotate-90 text-[9px]" />
            </button>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={handleCloseModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
              <div className="relative z-10 flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-black tracking-tighter leading-tight mb-1">Project Command Center</h3>
                  <p className="text-[9px] font-black uppercase tracking-[0.2em] text-blue-100 italic">Assignment intel & mission status</p>
                </div>
                <button onClick={handleCloseModal} className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center hover:bg-black/20 transition-all"><FaTimes className="text-xs" /></button>
              </div>
            </div>

            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Basic Intel */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Subject</p>
                  <p className="text-[11px] font-black text-slate-800 leading-tight italic">"{selectedConference.conferenceDetails?.topic || "N/A"}"</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Operational Timing</p>
                  <p className="text-[11px] font-black text-slate-800 tabular-nums italic">{selectedConference.conferenceDetails?.conferenceDate} at {selectedConference.conferenceDetails?.conferenceTime}</p>
                </div>
              </div>

              {/* Location Intel */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100/50 flex items-center justify-center text-blue-600 shrink-0"><FaMapMarkerAlt /></div>
                <div className="min-w-0">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Tactical Deployment Area</p>
                  <p className="text-[10px] font-bold text-slate-600 line-clamp-1 italic">{selectedConference.conferenceDetails?.city}, {selectedConference.conferenceDetails?.state}</p>
                  <p className="text-[11px] font-black text-slate-900 mt-0.5 truncate uppercase tracking-tight">{selectedConference.conferenceDetails?.place} ({selectedConference.conferenceDetails?.landmark})</p>
                </div>
              </div>

              {/* Brief */}
              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">Mission Intelligence</p>
                <div className="text-[10px] font-bold text-slate-600 leading-relaxed italic">
                  <DescriptionViewer description={selectedConference.conferenceDetails?.purpose || "No specific brief available."} wordLimit={20} textSize="text-[10px]" buttonText="Read Full Intel" modalTitle="Mission Intel" />
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 flex items-center justify-end gap-2 px-6 border-t border-slate-100/50">
              <button onClick={handleCloseModal} className="h-10 px-6 bg-white border border-slate-200 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest">Acknowledge</button>
              {(!selectedConference.proofSubmitted || selectedConference.proofDetails?.adminRejectNote) && (
                <button onClick={() => { handleCloseModal(); handleSubmitProofClick(selectedConference.conferenceId); }} className="h-10 px-6 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-200 transition-all active:scale-95">Action Intel</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Proof Submission Modal */}
      {showModal && !selectedConference && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-xl" onClick={handleCloseModal} />
          <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
            <div className="bg-slate-900 p-7 text-white">
              <h3 className="text-xl font-black tracking-tighter mb-1 select-none">Mission Finalization</h3>
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Submit cryptographic proof of coverage</p>
            </div>

            <form onSubmit={handleSubmitProof} className="p-7 space-y-5 max-h-[75vh] overflow-y-auto custom-scrollbar">
              <div className="space-y-4">
                <div className="relative group">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Platform Intel</p>
                  <select name="platform" value={formData.platform} onChange={handleInputChange} className="w-full h-12 px-5 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 focus:outline-none focus:border-blue-200 transition-all appearance-none cursor-pointer">
                    <option value="">Select Platform</option>
                    {availablePlatforms.length > 0 ? (
                      availablePlatforms.map((platform, idx) => (
                        <option key={idx} value={platform}>
                          {platform}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="Youtube">YouTube Mainframe</option>
                        <option value="Facebook">Meta Network</option>
                        <option value="Instagram">Social Grid</option>
                        <option value="Twitter">X Intelligence</option>
                        <option value="Other">External Protocol</option>
                      </>
                    )}
                  </select>
                  <div className="absolute right-5 bottom-4 pointer-events-none text-slate-300 text-[10px]"><FaClock /></div>
                </div>

                <div className="relative">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Channel Signature</p>
                  <input type="text" name="channelName" value={formData.channelName} onChange={handleInputChange} placeholder="Deployment Target Channel..." className="w-full h-12 px-5 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 focus:outline-none focus:border-blue-200 transition-all" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Duration Intel</p>
                    <input type="text" name="duration" value={formData.duration} onChange={handleInputChange} placeholder="MM:SS" className="w-full h-12 px-5 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 tabular-nums focus:outline-none focus:border-blue-200 transition-all" />
                  </div>
                  <div className="relative">
                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Mission Code</p>
                    <input type="text" value={formData.conferenceId} disabled className="w-full h-12 px-5 bg-slate-200/50 border-2 border-transparent rounded-2xl text-[11px] font-black text-slate-500 tabular-nums" />
                  </div>
                </div>

                <div className="relative">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Coverage Uplink (YouTube Link)</p>
                  <div className="relative flex items-center">
                    <input type="url" name="videoLink" value={formData.videoLink} onChange={handleInputChange} placeholder="https://youtube.com/watch?v=..." className="w-full h-12 px-5 bg-slate-50 border-2 border-slate-100 rounded-2xl text-[11px] font-bold text-slate-700 focus:outline-none focus:border-blue-200 transition-all pr-12" />
                    <FaYoutube className="absolute right-5 text-rose-500 text-lg" />
                  </div>
                </div>

                <div className="relative">
                  <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Coverage Visual Proof</p>
                  <div className={`relative w-full border-2 border-dashed rounded-[2rem] transition-all flex flex-col items-center justify-center p-6 ${screenshotPreview ? 'border-blue-200 bg-blue-50/20' : 'border-slate-100 bg-slate-50'}`}>
                    {screenshotPreview ? (
                      <div className="relative w-full">
                        <img src={screenshotPreview} alt="Preview" className="w-full h-32 object-cover rounded-2xl shadow-lg border border-white" />
                        <button type="button" onClick={removeScreenshot} className="absolute -top-2 -right-2 w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-all"><FaTimes className="text-xs" /></button>
                      </div>
                    ) : (
                      <>
                        <input type="file" name="screenshot" accept="image/*" onChange={handleFileChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                        <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-3">
                          <FaUpload className="text-blue-500 text-xs animate-bounce" />
                        </div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-tighter text-center">Drag visual intel here or <span className="text-blue-500">browse</span></p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-2 flex items-center gap-3">
                <button type="submit" className="flex-[2] h-14 bg-slate-900 text-white rounded-2xl text-[12px] font-black uppercase tracking-[0.2em] shadow-2xl active:scale-95 transition-all">Upload Proof</button>
                <button type="button" onClick={handleCloseModal} className="flex-1 h-14 bg-white border border-slate-200 text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-widest">Abandon</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" theme="dark" hideProgressBar />
    </div>
  );
};

export default AcceptedFreeConference;
